import type Department from "../../types/Department";
import type DepartmentGroup from "../../types/DepartmentGroup";

export type ListViewKind = "department" | "group";

// A board in the View picker: a department (id = its name) or a department
// group (id = its numeric id, as a string).
export interface ListViewOption {
  kind: ListViewKind;
  id: string;
  label: string;
  hospitalID: number | null;
  isHospitalWide: boolean | null;
  eddWindow: string | null;
}

// A selected board, however it was chosen: an option, or a link's params.
export interface ListView {
  kind?: ListViewKind;
  id: string | null;
  hospitalID: number | null;
}

export const buildViewOptions = (
  departments: Department[],
  departmentGroups: DepartmentGroup[]
): ListViewOption[] => {
  const deptNames = departments.map((d) => ({
    kind: "department" as const,
    id: d.department_name,
    label: d.department_display_name
      ? d.department_display_name
      : d.department_name,
    hospitalID: d.qv_hospital_id,
    isHospitalWide: d.is_hospital_wide,
    eddWindow: d.edd_window,
  }));

  const deptGroupNames = departmentGroups.map((dg) => ({
    kind: "group" as const,
    id: String(dg.id),
    label: dg.group_name,
    hospitalID: null,
    isHospitalWide: null,
    eddWindow: null,
  }));

  return [...deptNames, ...deptGroupNames].sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

// The list endpoints take ?group= for a group and ?department=&qv_hospital_id=
// for a unit.
export const viewQueryParams = (
  view: ListView
): { department?: string; group?: string; hospitalID?: number } => {
  const isGroup = view.kind === "group";
  return {
    department: isGroup ? undefined : view.id ?? undefined,
    group: isGroup ? view.id : undefined,
    hospitalID: view.hospitalID ? view.hospitalID : undefined,
  };
};

// The picker option for a board. A unit is matched with its hospital too,
// since the same department name can exist at more than one.
export const findViewOption = (
  options: ListViewOption[],
  view: ListView
): ListViewOption | null =>
  options.find(
    (option) =>
      option.kind === view.kind &&
      option.id === view.id &&
      (option.kind === "group" || option.hospitalID === view.hospitalID)
  ) ?? null;
